/**
 * The child grammar — which node kinds may sit in which child slot.
 *
 * Task 0030's complaint, and it is accurate: `validateSpec` says yes at nearly
 * every position for nearly every node, because `region.children`,
 * `card.children`, `collection.items[].spec` and `capability.slots[*]` are all
 * `Array<Self>` in the schema. A recursive union cannot express "not here".
 *
 * That is tolerable while specs are authored by a generator and read by a
 * renderer. It stops being tolerable the moment a user DRAGS something: the
 * highlight in 0031 is drawn from legality, so a grammar that says yes
 * everywhere paints the screen uniformly green and teaches the user that the
 * highlight means nothing.
 *
 * **Named, not computed.** Closed set to closed set, per decision 0015 (S26).
 * A table a human can read and a drop engine can index in O(1), rather than a
 * predicate that has to be run and then explained.
 */

import type { TNode } from './schema.js';

export const NODE_KINDS = [
  'region', 'collection', 'card', 'field', 'media', 'node-slot', 'capability',
] as const;
export type NodeKind = (typeof NODE_KINDS)[number];

/** Kinds that carry a value and cannot host anything. Stated once, here,
 *  rather than discovered by finding no matching slot.
 *
 *  `node-slot` left this set on 2026-08-23. It carried a value — a relation —
 *  and drew it with a preview the RENDERER chose the shape of: a thumbnail, a
 *  name, and three metadata rows picked by `.slice(0, 3)`. That is presentation
 *  decided in the one place this product says presentation is never decided,
 *  and it is what `canon-notion.md` means by *"the core must be recursive — a
 *  region must be able to host a complete spec."* */
export const LEAF_KINDS = ['field', 'media'] as const;

export interface ChildSlot {
  readonly host: NodeKind;
  /** The slot's name in the spec, which is also its name in a drop candidate. */
  readonly slot: 'children' | 'item' | 'slot';
  readonly admits: readonly NodeKind[];
  /** The schema's own `maxItems` for this slot, restated so a drop engine can
   *  refuse a drop that would exceed it WITHOUT constructing the spec first. */
  readonly max: number;
}

/**
 * Everything that is not a collection may be an item template.
 *
 * A `collection` inside `collection.items[].spec` is the one containment rule
 * with a measured cost rather than an aesthetic one: it validates today, and it
 * renders the whole object set inside every card — sixty rows each containing
 * sixty rows is 3600 card renders from one legal drop. Refused at the slot AND
 * anywhere in the subtree below it, because nesting it one level deeper costs
 * exactly the same.
 */
const NOT_COLLECTION = NODE_KINDS.filter((k) => k !== 'collection');

export const CHILD_SLOTS: readonly ChildSlot[] = [
  { host: 'region', slot: 'children', admits: NODE_KINDS, max: 64 },
  { host: 'collection', slot: 'item', admits: NOT_COLLECTION, max: 8 },
  { host: 'card', slot: 'children', admits: NODE_KINDS, max: 16 },
  { host: 'capability', slot: 'slot', admits: NODE_KINDS, max: 32 },
  /**
   * What a relation is drawn AS.
   *
   * `NOT_COLLECTION` for the reason the item template uses it, and the cost is
   * worse here: a collection inside a node-slot renders the whole object set
   * once per REFERENCED object, and a node-slot's `maxDepth` lets that recur.
   * Sixty rows inside sixty previews inside sixty rows is a legal spec nobody
   * would survive.
   *
   * `max: 16` matches `card.children` rather than `region.children`, because
   * what belongs here is a card's worth of an object — the thing a preview was
   * approximating — and not a page of one.
   */
  { host: 'node-slot', slot: 'children', admits: NOT_COLLECTION, max: 16 },
];

const BY_HOST = new Map<string, ChildSlot>(
  CHILD_SLOTS.map((s) => [`${s.host}.${s.slot}`, s]),
);

/** The slots a kind exposes. Empty for a leaf, which is the answer a drop
 *  engine needs before it considers a position at all. */
export function slotsOf(kind: NodeKind): readonly ChildSlot[] {
  return CHILD_SLOTS.filter((s) => s.host === kind);
}

export const isLeaf = (kind: NodeKind): boolean =>
  (LEAF_KINDS as readonly string[]).includes(kind);

/**
 * May `child` sit in `host`'s named slot?
 *
 * Two table lookups and an array membership test over a seven-word set. It is
 * deliberately ignorant of the surrounding tree: the subtree rule that refuses
 * a collection anywhere under an item template is a walk, and belongs to the
 * validator, not here. This answers the local question, fast, and says so.
 */
export function admits(host: NodeKind, slot: ChildSlot['slot'], child: NodeKind): boolean {
  const s = BY_HOST.get(`${host}.${slot}`);
  return s ? s.admits.includes(child) : false;
}

/**
 * Count JSON values, not spec nodes.
 *
 * 0030's point, and it is the sharper version of a bug this repo has already
 * hit twice: `MAX_NODES` in `validate.ts` counted objects reached by a walk
 * while `pool.ts` counted spec nodes in a different walk, and two counters kept
 * honest by a test is the bug rather than the fix. A spec node is not a unit of
 * work — a card with sixteen bindings is one node and thirty-four values — so
 * anything budgeting in nodes is budgeting in the wrong currency.
 *
 * Iterative with a WeakSet, like every other walk in this package: a cycle is a
 * refusal, not a stack overflow.
 */
export function countValues(value: unknown, cap = Number.POSITIVE_INFINITY): number {
  const stack: unknown[] = [value];
  const seen = new WeakSet<object>();
  let n = 0;
  while (stack.length) {
    const v = stack.pop();
    n += 1;
    if (n > cap) return n;
    if (v === null || typeof v !== 'object') continue;
    if (seen.has(v)) continue;   // a cycle is counted once; refusing it is preScan's job
    seen.add(v);
    if (Array.isArray(v)) {
      for (const c of v) stack.push(c);
    } else {
      for (const c of Object.values(v as Record<string, unknown>)) stack.push(c);
    }
  }
  return n;
}

/** The one budget. Counted in values, per `countValues`. */
export const MAX_VALUES = 20_000;

/** Which slot a child of this node occupies, for a walker that has the parent
 *  and needs the grammar entry. Returns undefined for a leaf. */
export function slotFor(node: TNode): ChildSlot['slot'] | undefined {
  switch (node.kind) {
    case 'region': return 'children';
    case 'collection': return 'item';
    case 'card': return 'children';
    case 'capability': return 'slot';
    case 'node-slot': return 'children';
    default: return undefined;
  }
}
